/**
 * checkDeviceRequirements.js
 * --------------------------
 * Runtime gatekeeper that decides whether the client device is allowed to run
 * the camera workflow.
 *
 * • Reads a **device_requirements** collection from Firestore.  
 *   Each doc’s **id** is an OS token (e.g. "Android", "iPhone OS") and the body
 *   must contain `{ minimum_os: <integer> }`.
 *
 * • Uses the browser **userAgent** string to extract the OS major version and
 *   compares it against the minimum required version.
 *
 * • Opens `navigator.mediaDevices.getUserMedia({ video:true })` to ensure the
 *   camera meets a baseline of **≥ 720p** and **≥ 30 fps**.
 *
 * Returns: `true`  → device passes all checks  
 *          `false` → any requirement failed
 *
 * NOTE:  This helper deliberately avoids throwing; errors are logged and
 *        the function resolves `false` so callers can decide whether to guide
 *        the user to a compatibility page.
 */
import { firebaseApp } from "./firebaseApp";
import { getFirestore, collection, getDocs } from "firebase/firestore";

export const checkDeviceRequirements = async (userAgent) => {
  // Flag we’ll flip to true only if *both* OS and camera tests pass.
  var meetsRequirements = false;

  try {
    // Ensure OS version is compatible
    const db = getFirestore(firebaseApp);
    const collectionRef = collection(db, "device_requirements");
    const querySnapshot = await getDocs(collectionRef);
    querySnapshot.forEach((doc) => {
      if (userAgent.includes(doc.id)) {
        const data = doc.data();
        const minimumOS = data.minimum_os;

        // Extract the numeric major version that follows the OS token.
        const osNumberString = userAgent.substring(
          userAgent.indexOf(doc.id) + doc.id.length,
        ).match(/^\d+|\d+\b|\d+(?=\w)/g)[0];

        const osNumber = parseInt(osNumberString, 10);
        // Compare against the minimum version declared in Firestore.

        if (osNumber >= minimumOS) {
          meetsRequirements = true;
        }
      }
    });

    // ---------- Hardware capability check ----------
    // Request camera AND microphone together so the browser shows a single
    // combined permission prompt up-front. The recording pod later reuses this
    // grant, so it never triggers a second (mic) prompt mid-flow.
    navigator.mediaDevices.getUserMedia({ video: true, audio: true })
      .then((stream) => {
        const videoTrack = stream.getVideoTracks()[0];
        const capabilities = videoTrack.getCapabilities();
        // Reject if camera is below 720p or slower than 30 fps.
        if (capabilities.aspectRatio < 720 || capabilities.frameRate < 30) { // set limit at 720p and 30fps
          meetsRequirements = false;
        }

        // Stop every track (camera + mic) — we only needed the grant + caps.
        stream.getTracks().forEach((track) => {
          try {
            track.stop();
          } catch {}
        });
      })
      .catch((error) => {
        console.error("Error accessing media devices:", error);

        // Failure to access camera → treat as incompatible but don’t throw.
      });
 
// Generic error wrapper: most likely Firestore/network failure.
} catch (error) {
  console.error("Error checking device requirements:", error);
}

  // Final verdict returned to caller.
  return meetsRequirements;
}